import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { BuyerUserQuery } from 'interfaceAdapter/queries/BuyerUser';
import { BuyerRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/BuyerRegistrationProfileInfo';
import { BuyerEditRepository } from 'interfaceAdapter/repositories/BuyerEdit';
import { useNavigate } from 'react-router-dom';
import { useLoading } from 'application/contexts/useLoading';
import { IAreaOption } from 'domain/entities/Buyer/Buyer';

const schema = yup
    .object({
        target_area: yup.array().of(yup.string()).required(), // 買収希望案件エリア
        target_company_industries: yup.array().of(yup.string()).required(), // 買収希望案件業種
        target_company_employee_size_lower: yup.string().nullable(), // 買収希望案件の従業員数（下限）
        target_company_employee_size_upper: yup.string().nullable(), // 買収希望案件の従業員数（上限）
        target_company_purchase_amount_lower: yup.string().nullable(), // 買収希望金額（下限）
        target_company_purchase_amount_upper: yup.string().nullable(), // 買収希望金額（上限）
        target_company_revenue_scale_lower: yup.string().nullable(), // 買収希望案件の売上高（下限）
        target_company_revenue_scale_upper: yup.string().nullable(), // 買収希望案件の売上高（上限）
        target_company_profit_level_lower: yup.string().nullable(), // 買収希望案件の営業利益（下限）
        target_company_profit_level_upper: yup.string().nullable(), // 買収希望案件の営業利益（上限）
        target_company_net_assets_level_lower: yup.string().nullable(), // 買収希望案件の純資産金額（下限）
        target_company_net_assets_level_upper: yup.string().nullable(), // 買収希望案件の純資産金額（上限）
        has_acquisition_experience: yup.string().nullable(), // 過去の買収実績があるか
        invitation_code: yup.string().max(100), // 招待コード
    })
    .required();

type BuyerForm = yup.InferType<typeof schema>;

export const useBuyerProfileRegistration = () => {
    const diContainer = useContext(diContainerContext);
    const buyerRegistrationProfileInfoQuery = diContainer.resolve(BuyerRegistrationProfileInfoQuery);
    const buyerUserQuery = diContainer.resolve(BuyerUserQuery);
    const buyerEditRepository = diContainer.resolve(BuyerEditRepository);
    const { loading, setLoading } = useLoading();
    const [areaChoices, setAreaChoices] = useState<IAreaOption[]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<string[][]>([]);
    const [employeeLowerChoices, setEmployeeLowerChoices] = useState<string[][]>([]);
    const [employeeUpperChoices, setEmployeeUpperChoices] = useState<string[][]>([]);
    const [netAssetsLevelLowerChoices, setNetAssetsLevelLowerChoices] = useState<string[][]>([]);
    const [netAssetsLevelUpperChoices, setNetAssetsLevelUpperChoices] = useState<string[][]>([]);
    const [purchaseAmountLowerChoices, setPurchaseAmountLowerChoices] = useState<string[][]>([]);
    const [purchaseAmountUpperChoices, setPurchaseAmountUpperChoices] = useState<string[][]>([]);
    const [revenueScaleLowerChoices, setRevenueScaleLowerChoices] = useState<string[][]>([]);
    const [revenueScaleUpperChoices, setRevenueScaleUpperChoices] = useState<string[][]>([]);
    const [profitLevelLowerChoices, setProfitLevelLowerChoices] = useState<string[][]>([]);
    const [profitLevelUpperChoices, setProfitLevelUpperChoices] = useState<string[][]>([]);
    const [hasAcquisitionExperienceChoices, setHasAcquisitionExperienceChoices] = useState<string[][]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<BuyerForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            target_area: [],
            target_company_industries: [],
            target_company_employee_size_lower: '',
            target_company_employee_size_upper: '',
            target_company_purchase_amount_lower: '',
            target_company_purchase_amount_upper: '',
            target_company_revenue_scale_lower: '',
            target_company_revenue_scale_upper: '',
            target_company_profit_level_lower: '',
            target_company_profit_level_upper: '',
            target_company_net_assets_level_lower: '',
            target_company_net_assets_level_upper: '',
            has_acquisition_experience: '',
            invitation_code: '',
        },
    });

    useEffect(() => {
        const checkCanEnter = async () => {
            const { status } = await buyerUserQuery.checkToken(true);

            if (status === false) {
                // 有効なトークンが無いのでリダイレクト
                navigate('/buyer/login/');
            }
        };
        checkCanEnter();
    }, [window.location]);

    useEffect(() => {
        const fetchData = async () => {
            const data = await buyerRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setEmployeeLowerChoices(data.employee_lower_choices);
            setEmployeeUpperChoices(data.employee_upper_choices);
            setRevenueChoices(data.revenue_choices);
            setIndustryChoices(data.industry_choices);
            setProfitLevelLowerChoices(data.profit_level_lower_choices);
            setProfitLevelUpperChoices(data.profit_level_upper_choices);
            setNetAssetsLevelLowerChoices(data.net_assets_level_lower_choices);
            setNetAssetsLevelUpperChoices(data.net_assets_level_upper_choices);
            setPurchaseAmountLowerChoices(data.purchase_amount_lower_choices);
            setPurchaseAmountUpperChoices(data.purchase_amount_upper_choices);
            setRevenueScaleLowerChoices(data.revenue_scale_lower_choices);
            setRevenueScaleUpperChoices(data.revenue_scale_upper_choices);
            setProfitLevelLowerChoices(data.profit_level_lower_choices);
            setProfitLevelUpperChoices(data.profit_level_upper_choices);
            setHasAcquisitionExperienceChoices(data.has_acquisition_experience_choices);
        };
        fetchData();
    }, []);

    const submit = handleSubmit(async (data) => {
        setLoading(true);

        try {
            await buyerEditRepository.edit({
                ...data,
            });
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/buyer/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        employeeLowerChoices,
        employeeUpperChoices,
        revenueChoices,
        industryChoices,
        profitLevelLowerChoices,
        profitLevelUpperChoices,
        netAssetsLevelLowerChoices,
        netAssetsLevelUpperChoices,
        purchaseAmountLowerChoices,
        purchaseAmountUpperChoices,
        revenueScaleLowerChoices,
        revenueScaleUpperChoices,
        hasAcquisitionExperienceChoices,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
    };
};
